import { getAnalytics } from "@tm/insurance/analytics";
import { CheckButton, ParaTag, RadioButton } from "@tm/static";
import { theme } from "@tm/utility/global";
import React, { useEffect } from "react";
import { FilterData, useFilterContext } from "../contexts/FilterContext";
import {
  CheckboxItem,
  Divider,
  FilterPlansContainer,
  FilterSection,
  FilterTitle,
  SumInsuredButton,
  SumInsuredSection,
} from "./FilterPlans.style";

// Define the data structure based on the mock data
interface FilterCriteria {
  key?: string;
  title: { text: { value: string } };
  quoteIds: string[];
}

interface FilterList {
  title: { text: { value: string } };
  key: string;
  type: "radio" | "checkbox" | "button";
  criteria: FilterCriteria[];
}

interface FilterPlansProps {
  filterList?: FilterList[];
}

const FilterPlans = ({ filterList = [] }: FilterPlansProps) => {
  const {
    currentSort,
    setSort,
    activeFilters,
    setFilter,
    setInitialFilterData,
  } = useFilterContext();
  const analytics = getAnalytics();
  // Initialize filter data in context
  useEffect(() => {
    if (filterList.length > 0) {
      const filterData: FilterData[] = filterList.map((filter) => ({
        filterId: filter.key,
        title: filter.title,
        key: filter.key,
        type: filter.type,
        criteria: filter.criteria,
        enabled: false,
      }));

      setInitialFilterData(filterData);
    }
  }, [filterList, setInitialFilterData]);

  // Handle sort filter changes
  const handleSortChange = (criteriaKey: string, criteriaTitle: string) => {
    // Use the criteria key directly - no more hardcoded mapping
    const sortKey = criteriaKey || null;
    analytics?.trackListingEvents({
      actionType: `${criteriaKey}_clicked`,
    });

    // For price filter, only allow single selection
    // If same sort is selected, deselect it (toggle behavior)
    // If different sort is selected, replace the current selection
    if (currentSort === sortKey) {
      setSort(null); // Deselect if clicking the same option
    } else {
      setSort(sortKey); // Select new option (automatically deselects others)
    }
  };

  // Handle regular filter changes
  const handleFilterChange = (
    filterKey: string,
    criteriaTitle: string,
    filterType?: "radio" | "checkbox" | "button"
  ) => {
    const filterId = `${filterKey}_${criteriaTitle}`;
    const isCurrentlyActive = activeFilters[filterId] || false;
    analytics?.trackListingEvents({
      actionType: `${criteriaTitle}_filter_clicked`,
    });

    // No special hardcoded logic for "All" - let defaultSelected handle the behavior

    // Checkbox filters allow multi-selection
    // Radio and Button filters are single-selection
    if (filterType === "checkbox") {
      // Multi-selection: simple toggle behavior
      setFilter(filterId, !isCurrentlyActive);
    } else if (filterType === "button") {
      // Button type filters: single selection behavior
      if (!isCurrentlyActive) {
        // Selecting a new option: clear others first, then set new one
        Object.keys(activeFilters).forEach((activeFilterId) => {
          if (
            activeFilterId.startsWith(`${filterKey}_`) &&
            activeFilters[activeFilterId]
          ) {
            setFilter(activeFilterId, false);
          }
        });
        setFilter(filterId, true);
      }
    } else if (filterType === "radio") {
      // Single-selection for radio filters: clear other selections first, then set new one
      Object.keys(activeFilters).forEach((activeFilterId) => {
        if (
          activeFilterId.startsWith(`${filterKey}_`) &&
          activeFilters[activeFilterId]
        ) {
          setFilter(activeFilterId, false);
        }
      });

      // Then set the new one (unless it was already active - toggle behavior)
      if (!isCurrentlyActive) {
        setFilter(filterId, true);
      }
    }
  };

  // Check if a sort option is selected
  const isSortSelected = (criteriaKey: string): boolean => {
    // Simply compare the criteria key with current sort
    return currentSort === criteriaKey;
  };

  // Check if a filter option is selected
  const isFilterSelected = (
    filterKey: string,
    criteriaTitle: string
  ): boolean => {
    const filterId = `${filterKey}_${criteriaTitle}`;
    return activeFilters[filterId] || false;
  };

  return (
    <FilterPlansContainer>
      {filterList.map((filter, filterIndex) => (
        <React.Fragment key={filter.key}>
          <FilterSection>
            <FilterTitle>{filter.title.text.value}</FilterTitle>

            {filter.type === "button" ? (
              <SumInsuredSection>
                {filter.criteria.map((criteria, criteriaIndex) => (
                  <SumInsuredButton
                    key={criteriaIndex}
                    isSelected={isFilterSelected(
                      filter.key,
                      criteria.title.text.value
                    )}
                    onClick={() =>
                      handleFilterChange(
                        filter.key,
                        criteria.title.text.value,
                        filter.type
                      )
                    }
                  >
                    {criteria.title.text.value}
                  </SumInsuredButton>
                ))}
              </SumInsuredSection>
            ) : (
              filter.criteria.map((criteria, criteriaIndex) => {
                if (!criteria.title.text.value) return null;

                const isSelected =
                  filter.key === "price"
                    ? isSortSelected(criteria?.key || "")
                    : isFilterSelected(filter?.key, criteria.title.text.value);

                const handleChange =
                  filter.key === "price"
                    ? () =>
                        handleSortChange(
                          criteria?.key || "",
                          criteria?.title?.text?.value || ""
                        )
                    : () =>
                        handleFilterChange(
                          filter.key,
                          criteria.title.text.value,
                          filter.type
                        );

                return (
                  <>
                    {filter.type === "checkbox" ? (
                      <CheckboxItem key={criteriaIndex}>
                        <CheckButton
                          inputId={`${filter.key}-${criteriaIndex}`}
                          isChecked={isSelected}
                          handleChange={(e) => {
                            e.preventDefault();
                            handleChange();
                          }}
                        >
                          <ParaTag
                            fontSize="sm"
                            color={theme.blackText}
                            margin="0"
                          >
                            {criteria.title.text.value}
                          </ParaTag>
                        </CheckButton>
                      </CheckboxItem>
                    ) : (
                      <CheckboxItem key={criteriaIndex}>
                        <RadioButton
                          inputId={`${filter.key}-${criteriaIndex}`}
                          activeBtn={isSelected}
                          inputIndex={criteriaIndex}
                          handleChange={(e) => {
                            handleChange();
                          }}
                        >
                          <ParaTag
                            fontSize="sm"
                            color={theme.blackText}
                            margin="0"
                          >
                            {criteria.title.text.value}
                          </ParaTag>
                        </RadioButton>
                      </CheckboxItem>
                    )}
                  </>
                );
              })
            )}
          </FilterSection>

          {filterIndex < filterList.length - 1 && <Divider />}
        </React.Fragment>
      ))}
    </FilterPlansContainer>
  );
};

export default FilterPlans;
